import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Tooltip,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Collapse,
  IconButton,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AssessmentIcon from '@mui/icons-material/Assessment';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../context/AuthContext';

const ALL_INSTANCES = [
  { key: 'LIVE', label: 'LIVE' },
  { key: 'TOPORB', label: 'TIER2' },
  { key: 'TIER3', label: 'TIER3' },
  { key: 'V1', label: 'OPTIONS' },
  { key: 'E_OPTIONS', label: 'E-OPTIONS' },
  { key: 'OPTIONS_PCS', label: 'OPTIONS PCS' },
  { key: 'GROK', label: 'GROK' },
  { key: 'FUTURES', label: 'Futures' },
];

const DAYS_OF_WEEK = [
  { idx: 0, label: 'Monday' },
  { idx: 1, label: 'Tuesday' },
  { idx: 2, label: 'Wednesday' },
  { idx: 3, label: 'Thursday' },
  { idx: 4, label: 'Friday' },
];

const INSTANCE_COLORS: Record<string, string> = {
  LIVE: '#4caf50',
  TIER2: '#2196f3',
  TIER3: '#9c27b0',
  OPTIONS: '#ff9800',
  'E-OPTIONS': '#00bcd4',
  'OPTIONS PCS': '#e91e63',
  GROK: '#ff5722',
  Futures: '#ffc107',
};

function formatDate(d: Date): string {
  return d.toISOString().split('T')[0];
}

interface SummaryData {
  days_traded: number;
  total_trades: number;
  avg_pnl_pct: number;
  avg_pnl_dollar: number;
  total_pnl_dollar: number;
  total_pnl_pct: number;
  win_rate: number;
  sharpe: number;
  profit_factor: number;
  std_pct: number;
  avg_equity: number;
}

interface DayBreakdown {
  avg_pct: number;
  avg_dollar: number;
  win_rate: number;
  sharpe: number;
  days: number;
  trades: number;
}

interface TimelineEntry {
  date: string;
  pnl_pct: number;
  pnl_dollar: number;
  cumulative_pct: number;
}

interface SymbolEntry {
  symbol: string;
  avg_pct: number;
  total_pct: number;
  total_dollar: number;
  trades: number;
  win_rate: number;
}

interface LongShortEntry {
  long_avg_pct: number;
  short_avg_pct: number;
  long_trades: number;
  short_trades: number;
  long_wr: number;
  short_wr: number;
}

interface ReportData {
  summary_by_instance: Record<string, SummaryData | null>;
  daily_breakdown: Record<string, Record<string, DayBreakdown>>;
  timeline: Record<string, TimelineEntry[]>;
  symbol_performance: Record<string, SymbolEntry[]>;
  long_vs_short: Record<string, Record<string, LongShortEntry>>;
  account_equities: Record<string, number>;
}

type SortDir = 'asc' | 'desc';

const ReportingView: React.FC = () => {
  const { token } = useAuth();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const [startDate, setStartDate] = useState(formatDate(thirtyDaysAgo));
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [selectedInstances, setSelectedInstances] = useState<string[]>(ALL_INSTANCES.map((i) => i.key));
  const [selectedDays, setSelectedDays] = useState<number[]>([0, 1, 2, 3, 4]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ReportData | null>(null);

  const [symbolFilter, setSymbolFilter] = useState('ALL');
  const [sortCol, setSortCol] = useState<string>('avg_pct');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    summary: true,
    heatmap: true,
    cumulative: true,
    daybar: true,
    longshort: true,
    symbols: true,
  });

  const toggleSection = (key: string) =>
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleInstance = (key: string) => {
    setSelectedInstances((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleDay = (idx: number) => {
    setSelectedDays((prev) =>
      prev.includes(idx) ? prev.filter((d) => d !== idx) : [...prev, idx]
    );
  };

  const runReport = async () => {
    if (!selectedInstances.length || !selectedDays.length) {
      setError('Select at least one instance and one day.');
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await fetch('/api/reports/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate,
          instances: selectedInstances,
          days_of_week: selectedDays,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail || `HTTP ${res.status}`);
      }
      const json: ReportData = await res.json();
      setData(json);
    } catch (e: any) {
      setError(e.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const activeInstances = useMemo(() => {
    if (!data) return [];
    return Object.keys(data.summary_by_instance).filter((k) => data.summary_by_instance[k] !== null);
  }, [data]);

  const allSymbols = useMemo(() => {
    if (!data) return [];
    const syms = new Set<string>();
    Object.values(data.symbol_performance).forEach((arr) =>
      arr.forEach((s) => syms.add(s.symbol))
    );
    return Array.from(syms).sort();
  }, [data]);

  const cumulativeChartData = useMemo(() => {
    if (!data) return [];
    const dateMap: Record<string, Record<string, number>> = {};
    activeInstances.forEach((inst) => {
      (data.timeline[inst] || []).forEach((e) => {
        if (!dateMap[e.date]) dateMap[e.date] = {};
        dateMap[e.date][inst] = e.cumulative_pct;
      });
    });
    return Object.keys(dateMap)
      .sort()
      .map((date) => ({ date, ...dateMap[date] }));
  }, [data, activeInstances]);

  const dayBarData = useMemo(() => {
    if (!data) return [];
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    return selectedDays
      .sort((a, b) => a - b)
      .map((idx) => {
        const entry: Record<string, any> = { day: dayNames[idx] };
        activeInstances.forEach((inst) => {
          const db = data.daily_breakdown[inst]?.[dayNames[idx]];
          entry[inst] = db ? db.avg_pct : 0;
        });
        return entry;
      });
  }, [data, activeInstances, selectedDays]);

  const longShortData = useMemo(() => {
    if (!data) return [];
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    const rows: any[] = [];
    selectedDays
      .sort((a, b) => a - b)
      .forEach((idx) => {
        activeInstances.forEach((inst) => {
          const ls = data.long_vs_short[inst]?.[dayNames[idx]];
          if (ls) {
            rows.push({
              day: dayNames[idx],
              instance: inst,
              long_avg: ls.long_avg_pct,
              short_avg: ls.short_avg_pct,
              long_wr: ls.long_wr,
              short_wr: ls.short_wr,
            });
          }
        });
      });
    return rows;
  }, [data, activeInstances, selectedDays]);

  const pctColor = (v: number) => (v > 0 ? '#4caf50' : v < 0 ? '#f44336' : '#999');

  const SectionHeader: React.FC<{ title: string; sectionKey: string }> = ({ title, sectionKey }) => (
    <Box
      sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1, mt: 3 }}
      onClick={() => toggleSection(sectionKey)}
    >
      <IconButton size="small">
        {expandedSections[sectionKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {title}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssessmentIcon fontSize="large" color="primary" />
        Performance Reporting
      </Typography>

      {/* ---------- FILTER PANEL ---------- */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedInstances(ALL_INSTANCES.map((i) => i.key))}
              >
                All
              </Button>
              <Button size="small" variant="outlined" onClick={() => setSelectedInstances([])}>
                None
              </Button>
              {ALL_INSTANCES.map((inst) => (
                <FormControlLabel
                  key={inst.key}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedInstances.includes(inst.key)}
                      onChange={() => toggleInstance(inst.key)}
                      sx={{ py: 0 }}
                    />
                  }
                  label={<Typography variant="body2">{inst.label}</Typography>}
                  sx={{ mr: 1 }}
                />
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedDays([0, 1, 2, 3, 4])}
              >
                All Days
              </Button>
              <Button size="small" variant="outlined" onClick={() => setSelectedDays([])}>
                None
              </Button>
              {DAYS_OF_WEEK.map((d) => (
                <FormControlLabel
                  key={d.idx}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedDays.includes(d.idx)}
                      onChange={() => toggleDay(d.idx)}
                      sx={{ py: 0 }}
                    />
                  }
                  label={<Typography variant="body2">{d.label}</Typography>}
                  sx={{ mr: 1 }}
                />
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
              onClick={runReport}
              disabled={loading}
              sx={{ borderRadius: 2, px: 4, fontWeight: 'bold' }}
            >
              {loading ? 'Running...' : 'Run Report'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography sx={{ mt: 2, color: 'text.secondary' }}>Fetching EOD data from servers...</Typography>
        </Box>
      )}

      {data && !loading && (
        <>
          {/* ========== SECTION 1: SUMMARY CARDS ========== */}
          <SectionHeader title="Instance Summary" sectionKey="summary" />
          <Collapse in={expandedSections.summary}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {activeInstances.map((inst) => {
                const s = data.summary_by_instance[inst]!;
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={inst}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        borderTop: `4px solid ${INSTANCE_COLORS[inst] || '#999'}`,
                        height: '100%',
                      }}
                    >
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {inst}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">Avg Daily</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: pctColor(s.avg_pnl_pct), textAlign: 'right' }}>
                            {s.avg_pnl_pct > 0 ? '+' : ''}{s.avg_pnl_pct.toFixed(3)}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Total P&L</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: pctColor(s.total_pnl_dollar), textAlign: 'right' }}>
                            ${s.total_pnl_dollar.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Win Rate</Typography>
                          <Typography variant="body2" sx={{ textAlign: 'right' }}>{s.win_rate}%</Typography>
                          <Typography variant="body2" color="text.secondary">Sharpe</Typography>
                          <Typography variant="body2" sx={{ textAlign: 'right' }}>{s.sharpe}</Typography>
                          <Typography variant="body2" color="text.secondary">Profit Factor</Typography>
                          <Typography variant="body2" sx={{ textAlign: 'right' }}>{s.profit_factor}</Typography>
                          <Typography variant="body2" color="text.secondary">Days</Typography>
                          <Typography variant="body2" sx={{ textAlign: 'right' }}>{s.days_traded}</Typography>
                          <Typography variant="body2" color="text.secondary">Trades</Typography>
                          <Typography variant="body2" sx={{ textAlign: 'right' }}>{s.total_trades}</Typography>
                          <Typography variant="body2" color="text.secondary">Avg Equity</Typography>
                          <Typography variant="body2" sx={{ textAlign: 'right' }}>
                            ${s.avg_equity.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Collapse>

          {/* ========== SECTION 2: HEATMAP ========== */}
          <SectionHeader title="Day-of-Week Heatmap" sectionKey="heatmap" />
          <Collapse in={expandedSections.heatmap}>
            <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Instance</TableCell>
                    {selectedDays.sort((a, b) => a - b).map((idx) => (
                      <TableCell key={idx} align="center" sx={{ fontWeight: 'bold' }}>
                        {DAYS_OF_WEEK.find((d) => d.idx === idx)?.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeInstances.map((inst) => (
                    <TableRow key={inst}>
                      <TableCell>
                        <Chip
                          label={inst}
                          size="small"
                          sx={{ bgcolor: INSTANCE_COLORS[inst] || '#666', color: '#fff', fontWeight: 'bold' }}
                        />
                      </TableCell>
                      {selectedDays.sort((a, b) => a - b).map((idx) => {
                        const dayName = DAYS_OF_WEEK.find((d) => d.idx === idx)?.label || '';
                        const db = data.daily_breakdown[inst]?.[dayName];
                        const avgPct = db?.avg_pct || 0;
                        const wr = db?.win_rate || 0;
                        const intensity = Math.min(Math.abs(avgPct) * 50, 1);
                        const bg =
                          avgPct > 0
                            ? `rgba(76, 175, 80, ${0.15 + intensity * 0.6})`
                            : avgPct < 0
                            ? `rgba(244, 67, 54, ${0.15 + intensity * 0.6})`
                            : 'transparent';
                        return (
                          <TableCell key={idx} align="center" sx={{ bgcolor: bg, minWidth: 100 }}>
                            <Tooltip title={`${db?.days || 0} days sampled`}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: pctColor(avgPct) }}>
                                  {avgPct > 0 ? '+' : ''}{avgPct.toFixed(3)}%
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  WR {wr}%
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>

          {/* ========== SECTION 3: CUMULATIVE RETURNS ========== */}
          <SectionHeader title="Cumulative Returns" sectionKey="cumulative" />
          <Collapse in={expandedSections.cumulative}>
            <Paper sx={{ p: 2, mb: 2, borderRadius: 3, height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={cumulativeChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v: number) => `${v.toFixed(1)}%`} tick={{ fontSize: 11 }} />
                  <RechartsTooltip
                    contentStyle={{ background: '#1e1e1e', border: '1px solid #444', borderRadius: 8 }}
                    formatter={(value: number, name: string) => [`${value.toFixed(3)}%`, name]}
                  />
                  <Legend />
                  {activeInstances.map((inst) => (
                    <Line
                      key={inst}
                      type="monotone"
                      dataKey={inst}
                      stroke={INSTANCE_COLORS[inst] || '#999'}
                      dot={false}
                      strokeWidth={2}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Collapse>

          {/* ========== SECTION 4: DAY-OF-WEEK BAR CHART ========== */}
          <SectionHeader title="Day-of-Week Comparison" sectionKey="daybar" />
          <Collapse in={expandedSections.daybar}>
            <Paper sx={{ p: 2, mb: 2, borderRadius: 3, height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dayBarData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v: number) => `${v.toFixed(2)}%`} tick={{ fontSize: 11 }} />
                  <RechartsTooltip
                    contentStyle={{ background: '#1e1e1e', border: '1px solid #444', borderRadius: 8 }}
                    formatter={(value: number, name: string) => [`${value.toFixed(3)}%`, name]}
                  />
                  <Legend />
                  {activeInstances.map((inst) => (
                    <Bar key={inst} dataKey={inst} fill={INSTANCE_COLORS[inst] || '#999'} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Collapse>

          {/* ========== SECTION 5: LONG VS SHORT ========== */}
          <SectionHeader title="Long vs Short Performance" sectionKey="longshort" />
          <Collapse in={expandedSections.longshort}>
            <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Day</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Instance</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Long Avg %</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Long WR</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Short Avg %</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Short WR</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {longShortData.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{row.day}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.instance}
                          size="small"
                          sx={{ bgcolor: INSTANCE_COLORS[row.instance] || '#666', color: '#fff', fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ color: pctColor(row.long_avg), fontWeight: 'bold' }}>
                        {row.long_avg > 0 ? '+' : ''}{row.long_avg.toFixed(3)}%
                      </TableCell>
                      <TableCell align="center">{row.long_wr}%</TableCell>
                      <TableCell align="center" sx={{ color: pctColor(row.short_avg), fontWeight: 'bold' }}>
                        {row.short_avg > 0 ? '+' : ''}{row.short_avg.toFixed(3)}%
                      </TableCell>
                      <TableCell align="center">{row.short_wr}%</TableCell>
                    </TableRow>
                  ))}
                  {longShortData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">No long/short data available</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>

          {/* ========== SECTION 6: SYMBOL PERFORMANCE TABLE ========== */}
          <SectionHeader title="Symbol Performance" sectionKey="symbols" />
          <Collapse in={expandedSections.symbols}>
            <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Symbol Filter</InputLabel>
                  <Select
                    value={symbolFilter}
                    label="Symbol Filter"
                    onChange={(e) => setSymbolFilter(e.target.value)}
                  >
                    <MenuItem value="ALL">All Symbols</MenuItem>
                    {allSymbols.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                  {allSymbols.length} symbols across {activeInstances.length} instances
                </Typography>
              </Box>

              <TableContainer sx={{ maxHeight: 500 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>
                        <TableSortLabel
                          active={sortCol === 'symbol'}
                          direction={sortCol === 'symbol' ? sortDir : 'asc'}
                          onClick={() => {
                            setSortDir(sortCol === 'symbol' && sortDir === 'asc' ? 'desc' : 'asc');
                            setSortCol('symbol');
                          }}
                        >
                          Symbol
                        </TableSortLabel>
                      </TableCell>
                      {activeInstances.map((inst) => (
                        <React.Fragment key={inst}>
                          <TableCell align="center" sx={{ fontWeight: 'bold', borderLeft: '1px solid #444' }}>
                            <Chip
                              label={inst}
                              size="small"
                              sx={{ bgcolor: INSTANCE_COLORS[inst] || '#666', color: '#fff', fontWeight: 'bold' }}
                            />
                            <br />
                            <TableSortLabel
                              active={sortCol === `${inst}_avg`}
                              direction={sortCol === `${inst}_avg` ? sortDir : 'desc'}
                              onClick={() => {
                                setSortDir(sortCol === `${inst}_avg` && sortDir === 'desc' ? 'asc' : 'desc');
                                setSortCol(`${inst}_avg`);
                              }}
                            >
                              Avg %
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Trades</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>WR</TableCell>
                        </React.Fragment>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const symMap: Record<string, Record<string, SymbolEntry>> = {};
                      activeInstances.forEach((inst) => {
                        (data.symbol_performance[inst] || []).forEach((se) => {
                          if (symbolFilter !== 'ALL' && se.symbol !== symbolFilter) return;
                          if (!symMap[se.symbol]) symMap[se.symbol] = {};
                          symMap[se.symbol][inst] = se;
                        });
                      });

                      let rows = Object.entries(symMap);
                      rows.sort(([aSymbol, aData], [bSymbol, bData]) => {
                        if (sortCol === 'symbol') {
                          return sortDir === 'asc'
                            ? aSymbol.localeCompare(bSymbol)
                            : bSymbol.localeCompare(aSymbol);
                        }
                        const instKey = sortCol.replace('_avg', '');
                        const aVal = aData[instKey]?.avg_pct || 0;
                        const bVal = bData[instKey]?.avg_pct || 0;
                        return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
                      });

                      return rows.slice(0, 200).map(([symbol, instData]) => (
                        <TableRow key={symbol} hover>
                          <TableCell sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>{symbol}</TableCell>
                          {activeInstances.map((inst) => {
                            const se = instData[inst];
                            if (!se) {
                              return (
                                <React.Fragment key={inst}>
                                  <TableCell align="center" sx={{ borderLeft: '1px solid #333', color: '#555' }}>-</TableCell>
                                  <TableCell align="center" sx={{ color: '#555' }}>-</TableCell>
                                  <TableCell align="center" sx={{ color: '#555' }}>-</TableCell>
                                </React.Fragment>
                              );
                            }
                            return (
                              <React.Fragment key={inst}>
                                <TableCell
                                  align="center"
                                  sx={{
                                    borderLeft: '1px solid #333',
                                    fontWeight: 'bold',
                                    color: pctColor(se.avg_pct),
                                  }}
                                >
                                  <Tooltip title={`Total: $${se.total_dollar.toLocaleString()}`}>
                                    <span>{se.avg_pct > 0 ? '+' : ''}{se.avg_pct.toFixed(3)}%</span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="center">{se.trades}</TableCell>
                                <TableCell align="center">{se.win_rate}%</TableCell>
                              </React.Fragment>
                            );
                          })}
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Collapse>
        </>
      )}
    </Box>
  );
};

export default ReportingView;
